import { getCurrentTimeString, getTodayString } from './dateHelpers';

/**
 * Query helper functions for consistent database filtering
 */

/**
 * Add time-based filter to only show shifts that haven't started yet
 * Teachers can only apply to shifts that haven't started
 * @param query - Supabase query builder
 * @returns Modified query with time-based filter
 */
export function addApplicableShiftsFilter(query: any) {
  const today = getTodayString();
  const currentTime = getCurrentTimeString();
  
  // Only show shifts that:
  // 1. Are in the future (shift_date > today), OR
  // 2. Are today but haven't started yet (shift_date = today AND time_from > current_time)
  return query.or(
    `shift_date.gt.${today},and(shift_date.eq.${today},time_from.gt.${currentTime})`
  );
}

/**
 * Add time-based filter to show shifts that are still active (haven't ended yet)
 * @param query - Supabase query builder
 * @returns Modified query with active shifts filter
 */
export function addActiveShiftsFilter(query: any) {
  const today = getTodayString();
  const currentTime = getCurrentTimeString();
  
  // Only show shifts that:
  // 1. Are in the future (shift_date > today), OR
  // 2. Are today but haven't ended yet (shift_date = today AND time_till > current_time)
  return query.or(
    `shift_date.gt.${today},and(shift_date.eq.${today},time_till.gt.${currentTime})`
  );
}

/**
 * Add current/future date filter to a Supabase query
 * Only returns shifts from today onwards
 * @param query - Supabase query builder
 * @param dateColumn - Column name for date filtering (default: 'shift_date')
 * @returns Modified query with date filter
 */
export function addCurrentDateFilter(query: any, dateColumn = 'shift_date') {
  return query.gte(dateColumn, getTodayString());
}

/**
 * Common shift query filters for schools
 * @param query - Supabase query builder
 * @param shiftId - Shift ID to filter by
 * @param schoolId - School ID to filter by
 * @param includeExpired - Whether to include expired shifts (default: false)
 * @returns Modified query with standard filters
 */
export function addSchoolShiftFilters(query: any, shiftId: string, schoolId: string, includeExpired = false) {
  const baseQuery = query
    .eq('id', shiftId)
    .eq('school_id', schoolId);
    
  if (includeExpired) {
    // Include all shifts from today onwards (for viewing historical data)
    return baseQuery.gte('shift_date', getTodayString());
  } else {
    // Only include active shifts (not expired)
    return addActiveShiftsFilter(baseQuery);
  }
}

/**
 * Common shift query filters for teachers
 * @param query - Supabase query builder
 * @param includeExpired - Whether to include expired shifts (default: false)
 * @returns Modified query with standard filters
 */
export function addTeacherShiftFilters(query: any, includeExpired = false) {
  if (includeExpired) {
    // Include all shifts from today onwards (for viewing historical data)
    return query.gte('shift_date', getTodayString());
  } else {
    // Only include shifts that teachers can still apply to
    return addApplicableShiftsFilter(query);
  }
} 